import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import { fetchCompanyBankAccounts } from "./Trade/bankAccountsFetch";
import { Search, RefreshCcw, Undo2, Eye, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../Product/customers.css";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function money(n) {
  return `₦${Number(n || 0).toLocaleString()}`;
}

function statusFromRow(r) {
  const bal = Number(r?.balance || 0);
  const paid = Number(r?.amount_paid || 0);
  const refunded = Number(r?.amount_refunded || 0);

  if (bal <= 0) return "PAID";
  if (paid - refunded > 0) return "PART PAID";
  return "UNPAID";
}

function badgeClassFromStatus(status) {
  if (status === "PAID") return "badge paid";
  if (status === "PART PAID") return "badge part";
  return "badge open";
}

function PurchasePaymentModal({
  open,
  onClose,
  companyId,
  purchase,
  onPosted,
  bankAccounts,
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [type, setType] = useState("PAYMENT");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [bankAccountId, setBankAccountId] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setBusy(false);
    setType(purchase?.__forceType || "PAYMENT");
    setAmount("");
    setDate(todayISO());
    setNotes("");
    setBankAccountId(purchase?.bank_account_id || "");
  }, [open, purchase]);

  async function post() {
    setErr("");
    if (!companyId) return setErr("Missing company.");
    if (!purchase?.id) return setErr("Missing purchase.");
    if (!bankAccountId) return setErr("Select bank account.");
    const amt = Number(amount || 0);
    if (!(amt > 0)) return setErr("Amount must be > 0.");
    if (!date) return setErr("Select date.");

    setBusy(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Not authenticated.");

      const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/purchases-payment-post`;
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          purchase_id: purchase.id,
          type,
          amount: amt,
          payment_date: date,
          bank_account_id: bankAccountId,
          notes: notes?.trim() || null,
        }),
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw || "Unknown error" };
      }
      if (!res.ok) throw new Error(json?.error || "Failed to post payment.");

      onPosted?.(json);
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Error");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="purchase-payment-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="purchase-payment-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="purchase-payment-head">
          <div>
            <p className="purchase-payment-eyebrow">
              {type === "PAYMENT" ? "Vendor Payment" : "Vendor Refund"}
            </p>

            <h2>{type === "PAYMENT" ? "Record Payment" : "Record Refund"}</h2>

            <p>
              Record supplier cash movement and update payables, bank balances
              and vendor history.
            </p>
          </div>

          <button
            className="purchase-payment-close"
            onClick={onClose}
            disabled={busy}
          >
            ✕
          </button>
        </div>

        <div className="purchase-payment-summary-grid">
          <div className="purchase-payment-summary-card">
            <span>Total Purchase</span>
            <strong>{money(purchase?.total)}</strong>
          </div>

          <div className="purchase-payment-summary-card">
            <span>Total Paid</span>
            <strong>{money(purchase?.amount_paid)}</strong>
          </div>

          <div className="purchase-payment-summary-card">
            <span>Total Refunded</span>
            <strong>{money(purchase?.amount_refunded)}</strong>
          </div>

          <div className="purchase-payment-summary-card">
            <span>Outstanding Balance</span>
            <strong>{money(purchase?.balance)}</strong>
          </div>
        </div>

        <div className="purchase-payment-form">
          <div className="purchase-payment-grid">
            <div className="purchase-payment-field">
              <label>Transaction Type</label>

              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                disabled={busy}
              >
                <option value="PAYMENT">PAYMENT (money paid)</option>
                <option value="REFUND">REFUND (money received back)</option>
              </select>

              <small>
                Select payment when money is paid to the vendor. Select refund
                when money is received back from the vendor.
              </small>
            </div>

            <div className="purchase-payment-field">
              <label>Transaction Date</label>

              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={busy}
              />

              <small>
                The accounting date this transaction should reflect in reports
                and ledgers.
              </small>
            </div>
          </div>

          <div className="purchase-payment-grid">
            <div className="purchase-payment-field">
              <label>Amount</label>

              <input
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={busy}
              />

              <small>
                Enter the exact amount paid to or refunded by the vendor.
              </small>
            </div>

            <div className="purchase-payment-field">
              <label>Bank / Cash Account</label>

              <select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                disabled={busy}
              >
                <option value="">Select bank or cash account...</option>
                {bankAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.account_number ? `${a.account_number} - ` : ""}
                    {a.name}
                  </option>
                ))}
              </select>

              <small>
                Select the bank or cash account used for this supplier payment
                or refund receipt.
              </small>
            </div>
          </div>

          <div className="purchase-payment-field">
            <label>Notes / Narration</label>

            <textarea
              rows={4}
              placeholder="Optional narration or internal note..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={busy}
            />

            <small>
              Add transfer reference, payment channel, refund reason or internal
              explanation.
            </small>
          </div>

          {err && <div className="purchase-payment-alert error">{err}</div>}
        </div>

        <div className="purchase-payment-footer">
          <button
            className="purchase-payment-btn secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>

          <button
            className="purchase-payment-btn primary"
            onClick={post}
            disabled={busy}
          >
            {busy
              ? "Posting..."
              : type === "PAYMENT"
              ? "Post Payment"
              : "Post Refund"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PurchaseHistory() {
  const { companyId } = useCompany();
  const nav = useNavigate();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  const [rows, setRows] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [selected, setSelected] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);

  async function load() {
    if (!companyId) return;
    setBusy(true);
    setErr("");

    try {
      const { data, error } = await supabase
        .from("v_purchase_history")
        .select(
          "id, purchase_date, total, payment_status, bank_account_id, amount_paid, amount_refunded, balance, vendors(name, company_name)"
        )
        .eq("company_id", companyId)
        .order("purchase_date", { ascending: false })
        .limit(200);

      if (error) throw error;
      setRows(data || []);

      const banks = await fetchCompanyBankAccounts(companyId);
      setBankAccounts(banks || []);
    } catch (e) {
      setErr(e?.message || "Failed to load purchases.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [companyId]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      const name = (r.vendors?.name || "").toLowerCase();
      const comp = (r.vendors?.company_name || "").toLowerCase();
      const date = String(r.purchase_date || "");
      return name.includes(s) || comp.includes(s) || date.includes(s);
    });
  }, [q, rows]);

  function openPayment(p) {
    setSelected({ ...p, __forceType: "PAYMENT" });
    setShowPayModal(true);
  }
  function openRefund(p) {
    setSelected({ ...p, __forceType: "REFUND" });
    setShowPayModal(true);
  }

  async function deletePurchase(rowOrId) {
    try {
      if (!companyId) throw new Error("Missing companyId");

      // ✅ Accept either row object or id string
      const purchaseId =
        typeof rowOrId === "string"
          ? rowOrId
          : rowOrId?.id ||
            rowOrId?.purchase_id ||
            rowOrId?.purchase_transaction_id;

      if (!purchaseId) {
        console.error(
          "deletePurchase called without valid purchase id:",
          rowOrId
        );
        alert(
          "Cannot delete: purchase id is missing on this row. Please refresh."
        );
        return;
      }

      const ok = window.confirm(
        "Delete this purchase? This removes journals and settlements."
      );
      if (!ok) return;

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Not authenticated.");

      const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/purchases-delete`;

      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          purchase_id: purchaseId,
        }),
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw || "Unknown error" };
      }

      if (!res.ok) {
        console.error("purchases-delete failed:", {
          status: res.status,
          json,
          companyId,
          purchaseId,
        });
        throw new Error(json?.error || `Delete failed (${res.status})`);
      }

      await load();
      alert("Purchase deleted successfully.");
    } catch (e) {
      console.error("deletePurchase error:", e);
      alert(e?.message || "Failed to delete purchase");
    }
  }

  return (
    <div className="purchase-history-page">
      {/* HERO */}
      <header className="purchase-history-hero">
        <div>
          <p className="purchase-history-eyebrow">
            Purchases, Payments & Refunds
          </p>

          <h1>Purchase History</h1>

          <p>
            Monitor vendor purchases, supplier payments, refunds and outstanding
            payables across your business.
          </p>
        </div>

        <div className="purchase-history-actions">
          <div className="purchase-search-wrap">
            <Search size={16} className="purchase-search-icon" />

            <input
              type="text"
              placeholder="Search purchases by vendor or date..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <button className="purchase-btn soft" onClick={load} disabled={busy}>
            <RefreshCcw size={16} />
            {busy ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </header>

      {err && <div className="purchase-alert error">{err}</div>}

      {/* SUMMARY */}
      <section className="purchase-summary-grid">
        <div className="purchase-summary-card">
          <span>Total Purchases</span>
          <strong>{filtered.length}</strong>
        </div>

        <div className="purchase-summary-card">
          <span>Total Purchase Value</span>
          <strong>
            {money(filtered.reduce((a, b) => a + Number(b.total || 0), 0))}
          </strong>
        </div>

        <div className="purchase-summary-card">
          <span>Total Paid</span>
          <strong>
            {money(
              filtered.reduce((a, b) => a + Number(b.amount_paid || 0), 0)
            )}
          </strong>
        </div>

        <div className="purchase-summary-card">
          <span>Outstanding Payables</span>
          <strong>
            {money(filtered.reduce((a, b) => a + Number(b.balance || 0), 0))}
          </strong>
        </div>
      </section>

      {/* TABLE */}
      <section className="purchase-table-panel">
        <div className="purchase-table-head">
          <div>
            <h3>Purchase Transactions</h3>

            <p>
              Review vendor balances, purchase payments and supplier refunds.
            </p>
          </div>
        </div>

        <div className="purchase-table-wrap">
          <table className="purchase-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Vendor</th>
                <th>Status</th>
                <th className="num">Total</th>
                <th className="num">Paid</th>
                <th className="num">Refunded</th>
                <th className="num">Balance</th>
                <th className="center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((r) => {
                const st = statusFromRow(r);

                return (
                  <tr key={r.id}>
                    <td>{r.purchase_date || "—"}</td>

                    <td>
                      <div className="purchase-vendor">
                        <strong>{r.vendors?.name || "—"}</strong>

                        <span>{r.vendors?.company_name || ""}</span>
                      </div>
                    </td>

                    <td>
                      <span className={badgeClassFromStatus(st)}>{st}</span>
                    </td>

                    <td className="num">{money(r.total)}</td>

                    <td className="num">
                      <span className="purchase-paid-value">
                        {money(r.amount_paid)}
                      </span>
                    </td>

                    <td className="num">
                      <span className="purchase-refund-value">
                        {money(r.amount_refunded)}
                      </span>
                    </td>

                    <td className="num">
                      <span className="purchase-balance-value">
                        {money(r.balance)}
                      </span>
                    </td>

                    <td className="center">
                      <div className="purchase-row-actions">
                        <button
                          className="purchase-action-btn"
                          onClick={() => nav(`/purchase/${r.id}`)}
                        >
                          <Eye size={15} />
                          View
                        </button>

                        {Number(r.balance || 0) > 0 && (
                          <button
                            className="purchase-action-btn success"
                            onClick={() => openPayment(r)}
                            disabled={busy}
                          >
                            Payment
                          </button>
                        )}

                        <button
                          className="purchase-action-btn warning"
                          onClick={() => openRefund(r)}
                          disabled={busy}
                        >
                          <Undo2 size={15} />
                          Refund
                        </button>

                        <button
                          className="purchase-action-btn danger"
                          onClick={() => deletePurchase(r)}
                          disabled={busy}
                        >
                          <Trash2 size={15} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {!busy && !filtered.length && (
            <div className="purchase-empty">
              <div className="purchase-empty-icon">📦</div>

              <h3>No purchases found</h3>

              <p>
                Purchase transactions will appear here after vendor purchases
                are recorded.
              </p>
            </div>
          )}
        </div>
      </section>

      <PurchasePaymentModal
        open={showPayModal}
        onClose={() => setShowPayModal(false)}
        companyId={companyId}
        purchase={selected}
        bankAccounts={bankAccounts}
        onPosted={() => load()}
      />
    </div>
  );
}
